/**
 * csvExport — small helpers for turning tabular data into a downloadable CSV file.
 */

type CsvCell = string | number | null | undefined;

/** Escape a single CSV cell — quote it when it contains a comma, quote, or newline. */
function escapeCell(value: CsvCell): string {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Build a CSV string from a header row and data rows. Cells are escaped and
 * rows joined with CRLF for Excel compatibility.
 */
export function toCsv(headers: string[], rows: CsvCell[][]): string {
  return [headers, ...rows]
    .map((row) => row.map(escapeCell).join(","))
    .join("\r\n");
}

/**
 * Trigger a browser download of `content` as `filename`. Prepends a UTF-8 BOM
 * so Excel renders non-ASCII characters correctly.
 */
export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob(["﻿", content], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Slugify a name for use in a filename: lowercase, alphanumerics joined by dashes. */
export function slugifyForFilename(name: string): string {
  return (
    name
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase() || "export"
  );
}
